import { Injectable } from '@angular/core';

import { BsModalService } from 'ngx-bootstrap/modal';

import { NotificationModalComponent } from '../modals/notification-modal/notification-modal.component';

export type ConfirmationConstructor = 'confirm' | 'yesNo' | 'ok' | 'cancelProcess';
export interface ConfirmationOptions {
	title: string;
	body: string;
	body2?: string;
	customClass?: string;
	customConfirmationButtonText?: string;
	confirmButtomClass?: string;
	cancelButtonClass?: string;
}

export interface InfoOptions {
	title: string;
	body: string;
	body2?: string;
}

@Injectable({
	providedIn: 'root',
})
export class NotificationService {
	constructor(private modalService: BsModalService) {}

	/**
	 * Open a confirmation modal
	 * @param options.initialState - The initial state of the modal
	 * @param options.confirmationType - The type of confirmation modal to open
	 * @param options.modalClass - The class to apply to the modal
	 * @param options.parentModalService - If you're already in a modal, pass this so that we avoid stacking issues
	 * @returns A promise that resolves to true if the user confirms, false otherwise
	 */
	public info({
		initialState,
		confirmationType = 'confirm',
		modalClass = 'pk-modal modal-lg',
		parentModalService,
	}: {
		initialState: InfoOptions;
		confirmationType?: ConfirmationConstructor;
		modalClass?: string;
		parentModalService?: BsModalService;
	}): void {
		(parentModalService ?? this.modalService).show(NotificationModalComponent, {
			class: modalClass,
			backdrop: 'static',
			initialState: {
				confirmationType,
				...initialState,
			},
		});
	}

	/**
	 * Open a confirmation modal
	 * @param options.initialState - The initial state of the modal
	 * @param options.confirmationType - The type of confirmation modal to open
	 * @param options.modalClass - The class to apply to the modal
	 * @param options.parentModalService - If you're already in a modal, pass this so that we avoid stacking issues
	 * @returns A promise that resolves to true if the user confirms, false otherwise
	 */
	public async confirm({
		initialState,
		confirmationType = 'confirm',
		modalClass = 'pk-modal modal-lg',
		parentModalService,
	}: {
		initialState: ConfirmationOptions;
		confirmationType?: ConfirmationConstructor;
		modalClass?: string;
		parentModalService?: BsModalService;
	}): Promise<boolean> {
		return new Promise((resolve, reject) => {
			try {
				let returnValue = false;
				const modalRef = (parentModalService ?? this.modalService).show(NotificationModalComponent, {
					class: modalClass,
					backdrop: 'static',
					initialState: {
						confirmationType,
						...initialState,
					},
				});
				modalRef.content.onConfirm.subscribe(() => {
					returnValue = true;
				});
				modalRef.content.onCancel.subscribe(() => {
					returnValue = false;
				});
				modalRef.onHidden.subscribe(() => {
					resolve(returnValue);
				});
			} catch (e) {
				reject(e.message);
			}
		});
	}
}
